"use client";

import { useEffect, useState } from "react";

import { clearSessionCookies, verifySessionCookies } from "@repo/ui/actions/session";
import { FlightSpinLoader } from "@repo/ui/components";
import { apiPath } from "@repo/ui/constants/bookingBase";
import { setupFetchInterceptor } from "@repo/ui/lib/fetchInterceptor";
import { clearTabSessionId, getTabSessionId, setTabSessionId } from "@repo/ui/lib/tabSessionId";
import { releaseDuplicatedTabSession } from "@repo/ui/lib/tabSessionOwnership";

import { cleanupReduxForTab } from "@repo/store/cleanup";
import ReduxProvider from "@repo/store/ReduxProvider";

import { markLogoutProcessed } from "./logoutSignal";

interface ReduxInitializerProps {
  children: React.ReactNode;
}

/**
 * ReduxInitializer ensures the session ID is ready BEFORE ReduxProvider/PersistGate mounts.
 * This eliminates the race condition between session initialization and redux-persist rehydration.
 *
 * Flow:
 * 1. Check if session ID exists
 * 2. If not, show loader and fetch session ID
 * 3. Wait for session to be ready
 * 4. Only then mount ReduxProvider (which triggers redux-persist)
 * 5. Redux-persist will now have session ID immediately available (no race condition)
 *
 * HYDRATION FIX: Uses client-only rendering to prevent SSR/CSR mismatches
 */
export default function ReduxInitializer({ children }: ReduxInitializerProps) {
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [hasProcessedLogout, setHasProcessedLogout] = useState(false);

  // Track mount state to prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Setup fetch interceptor to add x-tab-session-id header to requests
  useEffect(() => {
    setupFetchInterceptor();
  }, []);

  // Detect ?action=logout and wipe persisted Redux/session data before rehydration
  useEffect(() => {
    const cleanupForLogout = async () => {
      if (typeof window === "undefined") {
        setHasProcessedLogout(true);
        return;
      }

      const url = new URL(window.location.href);

      if (url.searchParams.get("action") !== "logout") {
        setHasProcessedLogout(true);
        return;
      }

      const existingSessionId = getTabSessionId();

      // Comprehensive cleanup: remove ALL Redux persist keys from localStorage
      // This ensures no old data persists into the new session
      try {
        const keysToRemove: string[] = [];

        // Collect all Redux/persist keys
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes("persist") || key.includes("redux") || key.includes("Redux"))) {
            keysToRemove.push(key);
          }
        }

        // Remove all collected keys
        keysToRemove.forEach((key) => {
          try {
            localStorage.removeItem(key);
          } catch {
            // Failed to remove key silently
          }
        });

        // Also try the specific cleanup function if we have a session ID
        if (existingSessionId) {
          cleanupReduxForTab(existingSessionId);
        }
      } catch (error) {
        console.warn("[ReduxInitializer] Failed to cleanup Redux data:", error);
      }

      clearTabSessionId();

      if (existingSessionId) {
        try {
          await clearSessionCookies(existingSessionId);
        } catch (error) {
          console.warn("[ReduxInitializer] Failed to clear session cookies:", error);
        }
      }

      if (typeof window !== "undefined") {
        sessionStorage.removeItem("last_activity_time");
      }

      // Tell LogoutStateReset (inside the provider) to clear the LIVE store too:
      // removing the keys above does not touch state redux-persist has already
      // rehydrated into memory, which is then re-persisted under the rotated key.
      markLogoutProcessed();

      setHasProcessedLogout(true);
    };

    void cleanupForLogout().finally(() => {
      const url = new URL(window.location.href);
      if (url.searchParams.get("action") !== "logout") return;

      url.searchParams.delete("action");
      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    });
  }, []);

  useEffect(() => {
    if (!hasProcessedLogout) {
      return;
    }

    async function initializeSession() {
      // A duplicated tab inherits the original session ID; drop it so this tab
      // bootstraps its own session with the default culture.
      const wasDuplicatedTab = await releaseDuplicatedTabSession();

      // Check if this tab already has a session ID
      let existingSessionId = getTabSessionId();
      let validation = existingSessionId ? await verifySessionCookies(existingSessionId) : null;

      if (validation?.cookieSessionId && validation.cookieSessionId !== existingSessionId) {
        setTabSessionId(validation.cookieSessionId);
        existingSessionId = validation.cookieSessionId;
        validation = await verifySessionCookies(existingSessionId);
      }

      if (existingSessionId) {
        if (validation?.valid) {
          setIsSessionReady(true);
          return;
        }

        clearTabSessionId();

        try {
          await clearSessionCookies(existingSessionId);
        } catch (error) {
          console.warn("[ReduxInitializer] Failed to clear session cookies:", error);
        }
      }

      // Allow session init on any page (anonymous or authenticated)
      if (typeof window !== "undefined") {
        try {
          const response = await fetch(apiPath("/api-site/init-session"));
          const data = await response.json();

          if (data.sessionId) {
            setTabSessionId(data.sessionId);

            window.dispatchEvent(
              new CustomEvent("session-initialized", { detail: { sessionId: data.sessionId } }),
            );

            if (wasDuplicatedTab) {
              // The page was server rendered with the inherited session, so
              // reload to render it with the new session's default culture.
              window.location.reload();
              return;
            }
          }
        } catch {
          window.dispatchEvent(new CustomEvent("session-initialized", { detail: { error: true } }));
        }
        setIsSessionReady(true);
      }
    }

    initializeSession();
  }, [hasProcessedLogout]);

  // During SSR or before mount, render children without Redux to prevent hydration mismatch
  // This ensures the server and initial client render are identical
  if (!isMounted || !hasProcessedLogout || !isSessionReady) {
    return (
      <div
        className="fixed inset-0 z-9999 flex items-center justify-center bg-white"
        style={{ pointerEvents: "all" }}
      >
        <FlightSpinLoader size="large" />
      </div>
    );
  }

  // Session is ready - now it's safe to initialize Redux
  // redux-persist will have session ID immediately available
  return <ReduxProvider>{children}</ReduxProvider>;
}
